import { formatPaymentData } from '@/features/income';
import {
    CreatorBalanceResponse,
    CurrenciesApiResponse,
    CurrencyInfo,
    OrderParamsRequest,
    OrderSearchRequest,
    OrderSearchResponse,
} from './types';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { User } from '@sentry/react';
import { copyToClipboard, downloadHtmlAsPDF } from '@/lib/helpers';
import { PAYMENT_STATUS, SENTRY_FEATURES } from '@/lib/constants';
import { mainApi } from '..';
import {
    CashOutAccountsResponse,
    CashOutResponse,
    PaymentSettingsResponse,
    TransactionPayload,
} from '@/types/payments';

const REVENUE_CHART_CONFIG = {
    /** Number of days to display in the revenue chart */
    DAY_RANGE: 30,
    LAST_DAY_INDEX: 29,
} as const;

export async function fetchOrders(params: OrderParamsRequest, signal?: AbortSignal) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const body: OrderSearchRequest = {
        page: params.page ?? 1,
        keyword: params.keyword,
        sortBy: params.sortBy,
        filter: params.filter,
        filter_array: params.filter_array ?? JSON.stringify([]),
        per_page: params.per_page ?? 15,
        timezone,
        user_fan_id: params.user_fan_id,
    };

    const res = await mainApi.post<OrderSearchResponse>('v1/payments/orders', body, {
        signal,
    });

    return res.data.all;
}

// API Functions
export async function getPaymentDetails(paymentId: number) {
    const response = await mainApi.get(`v1/payments/orders/${paymentId}`, {
        params: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
    });
    return formatPaymentData(response.data);
}

// TODO: Test
export async function resendDigitalDownload(paymentId: string): Promise<void> {
    await mainApi.post(`v1/orders/resend-digital-download/${paymentId}`);
}
// TODO: Test
// this is technically available for cancelled meetings as well.
// the income (payment) records don't have meeting status so it's tricky to disable it
// attention is needed if this becomes a creator feature (not restricted to admin anymore)
export async function resendBooking(paymentId: string) {
    try {
        await mainApi.post(`v1/orders/resend-booking/${paymentId}`, {});
        stanNotify({
            type: 'success',
            title: 'Resent Booking',
            text: 'Booking has been resent!',
            duration: 2000,
        });
    } catch (error: any) {
        stanNotify({
            type: 'error',
            title: 'Booking could not be sent, let us help you with that!',
            text: error?.response?.data?.message || 'Please email friends@stanwith.me',
            duration: 5000,
        });
    }
}
// TODO: Test
export async function sendReceipt(user: User, paymentId: string) {
    const payload = { payment_id: paymentId };

    stanAnalytics('send-receipt', {
        meta: { user_id: user?.user_id, username: user?.username },
        props: { payment_id: paymentId },
    });
    try {
        await mainApi.post('v1/payments/order/send-receipt', payload);
        stanNotify({
            type: 'success',
            title: 'Success!',
            text: 'Receipt Sent',
            duration: 5000,
        });
    } catch (error: any) {
        stanNotify({
            type: 'error',
            title: 'Oops.',
            text: 'Something went wrong. Please try again later.',
            duration: 5000,
            error: {
                message: '[INCOME] failed-to-send-receipt',
                options: {
                    tags: { feature: SENTRY_FEATURES.INCOME },
                    context: { error },
                },
            },
        });
    }
}

export async function downloadReceipt(user: User, paymentId: string) {
    const payload = { payment_id: paymentId };

    stanAnalytics('download-receipt', {
        meta: { user_id: user?.user_id, username: user?.username },
        props: { payment_id: paymentId },
    });
    try {
        const { data } = await mainApi.post('v1/payments/order/download-receipt', payload);
        if (data.receipt_html) {
            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Receipt Downloaded',
                duration: 5000,
            });
            downloadHtmlAsPDF(data.receipt_html, `Receipt ${paymentId}`);
        }
    } catch (error: any) {
        stanNotify({
            type: 'error',
            title: 'Oops.',
            text: 'Something went wrong. Please try again later.',
            duration: 5000,
            error: {
                message: '[INCOME] failed-to-download-receipt',
                options: {
                    tags: { feature: SENTRY_FEATURES.INCOME },
                    context: { error },
                },
            },
        });
    }
}

export async function copyUpdateBillingDetailsLink(paymentPlanId: string) {
    try {
        const { data } = await mainApi.post(`v1/payment-plan/${paymentPlanId}/billing-link`);
        await copyToClipboard(data?.link, {
            successMessage: 'URL Copied!',
            errorMessage: 'Failed to copy link to clipboard',
        });
    } catch (error) {
        console.error(error);
        stanNotify({
            type: 'error',
            title: 'Billing update link could not be generated, let us help you with that!',
            text: 'Please email friends@stanwith.me',
            duration: 4000,
        });
    }
}

export async function cancelPaymentPlan(paymentPlanId: string) {
    const payload = {
        action: 'cancel',
        payment_plan_id: paymentPlanId,
    };
    try {
        await mainApi.post('v1/payment-plan/manage', payload);
        stanNotify({
            type: 'success',
            title: 'Great!',
            text: 'Successfully cancelled Payment Plan.',
        });
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message.concat(' ', 'Please contact us at friends@stanwith.me');
        stanNotify({
            type: 'error',
            title: 'Something went wrong',
            text: errorMessage,
            error: {
                message: '[INCOME] failed-to-cancel-payment-plan',
                options: {
                    tags: { feature: SENTRY_FEATURES.INCOME },
                    context: { error },
                },
            },
        });
    }
}

export async function refundOrder(
    orderId?: string,
    token?: string | null,
    isAdminLoggedInAsUser?: boolean,
    user_id?: number | string
) {
    if (!orderId) {
        throw new Error('failed-to-refund-missing-order-id');
    }
    if (!token) {
        throw new Error('failed-to-refund-missing-token');
    }
    const payload: { payment_id: string; user_id?: number | string } = {
        payment_id: orderId,
    };

    const headers = {
        Authorization: `Bearer ${token}`,
    };
    let url = 'v1/payments/refund';

    if (isAdminLoggedInAsUser) {
        payload.user_id = user_id;
        url = 'v1/payments/admin-refund';
    }
    await mainApi.post(url, payload, { headers });
}

export async function partialRefundOrder(
    paymentId: string | undefined,
    amount: number,
    token: string | null,
    userId: number | string | undefined
) {
    if (!paymentId) {
        throw new Error('failed-to-partial-refund-missing-payment-id');
    }
    if (!token) {
        throw new Error('failed-to-partial-refund-missing-token');
    }
    if (userId === undefined || userId === null) {
        throw new Error('failed-to-partial-refund-missing-user-id');
    }

    const payload = {
        payment_id: Number(paymentId),
        amount,
        user_id: Number(userId),
    };

    await mainApi.post('v1/payments/partial-refund', payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

export async function fulfillOrder(orderId?: string | number, fileData?: Record<string, any>) {
    if (!orderId) {
        throw new Error('failed-to-fulfill-missing-order-id');
    }

    const payload: {
        transaction_id: string | number;
        file_name?: string;
        file_url?: string;
        no_attachments?: boolean;
    } = { transaction_id: orderId };

    if (fileData) {
        payload.file_name = fileData.name;
        payload.file_url = fileData.src;
    } else {
        payload.no_attachments = true;
    }
    await mainApi.patch('v1/orders/attachments', payload);
}

export async function confirmUnfulfillOrder(paymentId: string | number = -1) {
    // Shouldn't happen but logging in case
    if (paymentId === -1) {
        throw new Error('PaymentId must be passed');
    }
    const payload = { status: PAYMENT_STATUS.PAID_NOT_FULFILLED, payment_id: paymentId };
    await mainApi.patch('v1/payments/order/update-status', payload);
}

export async function resendOrder(emailToSendTo: string, orderId?: string | number | undefined) {
    if (!orderId) {
        throw new Error('failed-to-fulfill-missing-order-id');
    }
    const payload = { emailToSendTo: emailToSendTo, payment_id: orderId };
    await mainApi.post('v1/payments/order/resend', payload);
}

export async function sendPaymentFailedReminder(paymentId: string | number): Promise<void> {
    await mainApi.post(`v1/payments/order/${paymentId}/send_payment_failed_reminder`, null, {
        params: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
    });
}

export async function uploadDisputeEvidenceFile(file?: File) {
    const formData = new FormData();
    if (file) formData.append('file', file);
    const response = await mainApi.post('v1/payments/dispute/evidence/file', formData);
    return response.data;
}

export async function submitDispute(payload: {
    payment_id: string;
    customer_communication_file_id?: number;
    uncategorized_text?: string;
}) {
    const response = await mainApi.post('v1/payments/dispute/submit', payload);
    return response.data;
}

export async function fetchHasDisputeStatus(): Promise<boolean> {
    const payload = {
        page: 1,
        per_page: 1,
        filterArray: [{ fieldName: 'orders-status', value: 'DISPUTED' }],
    };

    const { data } = await mainApi.post('v1/payments/orders', payload);
    return data?.all?.total > 0;
}

export async function fetchBalance() {
    const { data } = await mainApi.get<CreatorBalanceResponse>('v1/payments/balance');
    return data;
}

export async function fetchCurrencies(): Promise<Record<string, CurrencyInfo>> {
    const { data } = await mainApi.get<CurrenciesApiResponse>('v1/payments/currencies');
    return data.currencies;
}

export async function fetchTransactions(payload: TransactionPayload) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await mainApi.post('v1/payments/orders', {
        ...payload,
        timezone,
        filter_array: JSON.stringify(payload.filterArray),
    });
    return data;
}

export async function fetchRevenueChart() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data } = await mainApi.get('v1/analytics/chart/cumulative-revenue', {
        params: {
            timezone,
            day_range: REVENUE_CHART_CONFIG.DAY_RANGE,
            period_length: 1,
            show_sum_series: 0,
        },
    });
    const dataSet = data.values;
    const labels = data.labels;
    const productNames = Object.keys(dataSet);

    const seriesData = [];

    if (productNames.length) {
        const totalRevenue: { 'Total Revenue': number[] } = { 'Total Revenue': [] };
        productNames.forEach(productName => {
            const pRevenue = dataSet[productName];
            if (Array.isArray(pRevenue)) {
                pRevenue.forEach((val: number, index: number) => {
                    totalRevenue['Total Revenue'][index] = (totalRevenue['Total Revenue'][index] ?? 0) + val;
                });
            }
        });
        seriesData.push({
            name: 'Total Revenue',
            data: totalRevenue['Total Revenue'].map(val => parseFloat(val.toFixed(2))),
            color: '#7675ff',
        });
    } else {
        seriesData.push({
            name: 'Total Revenue',
            data: labels.map(() => null),
            color: '#7675ff',
        });
    }

    const chartData = seriesData[0]?.data ?? [];
    const firstDataPoint = chartData[0] ?? 0;
    const lastDataPoint = chartData[REVENUE_CHART_CONFIG.LAST_DAY_INDEX] ?? chartData[chartData.length - 1] ?? 0;
    const yAxisMin = Math.max(0, firstDataPoint - (lastDataPoint - firstDataPoint) / 2);

    return {
        series: seriesData,
        options: {
            yaxis: {
                min: yAxisMin,
            },
            xaxis: {
                categories: labels,
            },
        },
    };
}

export async function fetchCashOutAccounts() {
    const { data } = await mainApi.get<CashOutAccountsResponse>('v1/payments/cash-out/accounts');
    return data;
}

export async function processCashOut() {
    const { data } = await mainApi.post<CashOutResponse>('v1/payments/cash-out', {});
    return data;
}

export async function fetchPaymentSettings() {
    const { data } = await mainApi.get<PaymentSettingsResponse>('v1/payments/settings');
    return data;
}
